// ============================================================================
// FILE: frontend/src/components/FileUploadModal.jsx
// Modal for managing client files (SharePoint links only)
// - Shows current SharePoint links
// - Unlinks (removes association) without deleting from SharePoint
// - Maintains a local list so counts update instantly
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";

/**
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - clientId: number
 *  - files: Array<{ id, file_name?, name?, sharepoint_url, file_type }>
 *  - onFileAdded?: (newFile) => void         // optional (if you attach via external picker)
 *  - onFileRemoved?: (fileId, newList) => void // optional; we still update local state regardless
 *  - onOpenSharePointPicker?: () => void     // optional; triggers your SharePoint picker
 */
export default function FileUploadModal({
  isOpen,
  onClose,
  clientId,
  files = [],
  onFileAdded,
  onFileRemoved,
  onOpenSharePointPicker,
}) {
  // Local copy so the UI updates instantly on unlink (and simple live count).
  const [localFiles, setLocalFiles] = useState(files);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null); // tracks unlink in progress

  // Keep local list in sync if parent replaces the prop (e.g., after refresh)
  useEffect(() => {
    setLocalFiles(files || []);
  }, [files]);

  const fileLimit = 5;
  const fileCount = localFiles.length;
  const atLimit = fileCount >= fileLimit;

  const title = useMemo(
    () => `Current Files (${fileCount}/${fileLimit})`,
    [fileCount]
  );

  const closeIfBackdrop = (e) => {
    // Only close if the click came from the backdrop, not inside the card
    if (e.target?.dataset?.backdrop === "true") onClose?.();
  };

  const openSharePointPicker = () => {
    if (typeof onOpenSharePointPicker === "function") {
      onOpenSharePointPicker();
    } else {
      // Non-blocking hint if no picker callback is provided
      console.warn(
        "[FileUploadModal] onOpenSharePointPicker not provided; add it to open your SharePoint picker."
      );
      setError(
        "SharePoint picker is not wired up yet. Provide onOpenSharePointPicker to enable selecting files."
      );
      setTimeout(() => setError(""), 3000);
    }
  };

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== "") {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === name + "=") {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };


  const handleUnlink = async (fileId) => {
    const ok = window.confirm(
      "This will unlink the file from the client but will not delete it in SharePoint. Continue?"
    );
    if (!ok) return;

    setBusyId(fileId);
    setError("");

    try {
      const csrfToken = getCookie("csrftoken");
      const resp = await fetch(`/api/sharepoint/files/${fileId}/unlink/`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });

      if (!resp.ok) throw new Error("Failed to unlink SharePoint file.");

      setLocalFiles((prev) => {
        const next = prev.filter((f) => f.id !== fileId);
        onFileRemoved?.(fileId, next);
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };


  // Renders a single file row (name/link + unlink button)
  const FileRow = ({ file }) => {
    const href = file?.sharepoint_url || file?.file_url || "#";
    const name = file?.file_name || file?.name || "Open in SharePoint";
    const type = (file?.file_type || "sharepoint").toString();

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 12,
          background: "#f9fafb",
          borderRadius: 8,
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#2563eb",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={name}
          >
            {name}
          </a>
          <div style={{ marginTop: 4, display: "flex", alignItems: "center" }}>
            <span
              style={{
                fontSize: 11,
                color: "#1e40af",
                background: "#dbeafe",
                padding: "2px 6px",
                borderRadius: 999,
                fontWeight: 600,
              }}
              title="File Type"
            >
              {type.toUpperCase()}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => handleUnlink(file.id)}
          disabled={busyId === file.id}
          style={{
            marginLeft: 12,
            padding: "6px 12px",
            background: busyId === file.id ? "#fecaca" : "#ef4444",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: busyId === file.id ? "not-allowed" : "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
          title="Unlink from client (does not delete in SharePoint)"
        >
          {busyId === file.id ? "Unlinking…" : "Unlink"}
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      data-backdrop="true"
      onClick={closeIfBackdrop}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          background: "white",
          borderRadius: 12,
          boxShadow:
            "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
          maxHeight: "88vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#ebf5ff",
              display: "grid",
              placeItems: "center",
            }}
            title="SharePoint"
          >
            {/* Simple link icon */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M10 13a5 5 0 0 0 7.07 0l3.54-3.54a5 5 0 0 0-7.07-7.07L11 4" />
              <path d="M14 11a5 5 0 0 0-7.07 0L3.39 14.54a5 5 0 0 0 7.07 7.07L13 20" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Manage Files</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              SharePoint links only. Unlinking will not delete from SharePoint.
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 8,
              borderRadius: 8,
            }}
            title="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6b7280"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 20,
            overflowY: "auto",
            flex: 1,
            display: "grid",
            gap: 16,
          }}
        >
          {/* Error box */}
          {error ? (
            <div
              role="alert"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Current Files */}
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 700 }}>{title}</div>

              {/* Add-from-SharePoint CTA */}
              <button
                type="button"
                onClick={openSharePointPicker}
                disabled={atLimit}
                style={{
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                  background: atLimit ? "#dbeafe" : "#2563eb",
                  color: atLimit ? "#1e3a8a" : "#fff",
                  cursor: atLimit ? "not-allowed" : "pointer",
                }}
                title={
                  atLimit
                    ? "Maximum of 5 files reached"
                    : "Add a link from SharePoint"
                }
              >
                {atLimit ? "Limit Reached" : "Add from SharePoint"}
              </button>
            </div>

            {localFiles.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#f9fafb",
                  border: "1px dashed #d1d5db",
                  borderRadius: 12,
                  color: "#6b7280",
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                No files linked yet. Click <b>Add from SharePoint</b> to attach
                a file.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {localFiles.map((file) => (
                  <FileRow key={file.id} file={file} />
                ))}
              </div>
            )}
          </section>

          {/* Note */}
          <section
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: 12,
              fontSize: 12,
              color: "#475569",
            }}
          >
            <b>Heads up:</b> Unlinking removes the association from this client
            only. The original file remains in SharePoint.
          </section>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 20px",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#111827",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
